import { useState } from 'react';
import { format } from 'date-fns';
import { CreditCardIcon, GaugeIcon, ArrowUpDownIcon } from 'lucide-react';
import type { Customer } from '@repo/types';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SectionCard, FieldRow, EmptyState, ToneBadge } from './_shared';
import { humanize } from './utils';
import { CustomerBillingHistory } from './CustomerBillingHistory';
import { ManageSubscriptionDialog } from './ManageSubscriptionDialog';

const fmtDate = (v?: string | Date | null) => (v ? format(new Date(v), 'MMM dd, yyyy') : null);

export function CustomerBillingTab({ customer }: { customer: Customer }) {
  const [manageOpen, setManageOpen] = useState(false);
  const sub = customer.subscription;
  const leadsTotal =
    customer.leadsConsumed != null && customer.leadsRemaining != null
      ? customer.leadsConsumed + customer.leadsRemaining
      : null;
  const leadsPct = leadsTotal ? Math.round(((customer.leadsConsumed ?? 0) / leadsTotal) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="space-y-5">
        {/* Subscription */}
        <SectionCard
          icon={CreditCardIcon}
          title="Subscription"
          action={
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setManageOpen(true)}>
              <ArrowUpDownIcon className="size-3.5" aria-hidden />
              {sub ? 'Change plan' : 'Assign plan'}
            </Button>
          }
        >
          {sub ? (
            <div className="divide-y divide-border/60">
              <FieldRow label="Plan">
                <ToneBadge tone="info">{sub.plan_name || `Tier ${sub.tier}`}</ToneBadge>
              </FieldRow>
              <FieldRow label="Status">
                <ToneBadge tone={sub.status === 'active' ? 'success' : 'neutral'}>{humanize(sub.status)}</ToneBadge>
              </FieldRow>
              {sub.current_period_end && <FieldRow label="Current period ends">{fmtDate(sub.current_period_end)}</FieldRow>}
              {sub.billing_cycle && <FieldRow label="Billing cycle">{humanize(sub.billing_cycle)}</FieldRow>}
              {sub.next_billing_date && <FieldRow label="Next billing">{fmtDate(sub.next_billing_date)}</FieldRow>}
            </div>
          ) : (
            <EmptyState icon={CreditCardIcon} message="No active subscription" />
          )}
        </SectionCard>

        <ManageSubscriptionDialog open={manageOpen} onOpenChange={setManageOpen} customer={customer} />

        {/* Usage */}
        <SectionCard icon={GaugeIcon} title="Usage">
          <div className="space-y-5">
            {leadsTotal != null ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Leads used</span>
                  <span className="font-semibold">
                    {customer.leadsConsumed} / {leadsTotal}
                  </span>
                </div>
                <Progress value={leadsPct} />
              </div>
            ) : null}
            {customer.profileCompleteness != null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Profile completeness</span>
                  <span className="font-semibold">{customer.profileCompleteness}%</span>
                </div>
                <Progress value={customer.profileCompleteness} />
              </div>
            )}
            {leadsTotal == null && customer.profileCompleteness == null && (
              <EmptyState icon={GaugeIcon} message="No usage data" />
            )}
          </div>
        </SectionCard>
      </div>

      {/* Billing history */}
      <CustomerBillingHistory customerId={customer.id} />
    </div>
  );
}
